import React, {
  useEffect,
  useMemo,
  useState,
  useRef,
  useCallback,
} from "react";
import { Card } from "react-bootstrap";
import { IconButton, Tooltip } from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import LaunchIcon from "@mui/icons-material/Launch";
import { ethers } from "ethers";
import Pagination from "../../Live/components/Pagenation";
import trinityManagerArtifact from "../../../../configs/abis/trinity-manager.json";
import config from "../../../../configs";
import { request } from "../../../../admin/plugins/axios";
import { endpoints } from "../../../../utils/networks";
import { getPurchaseHashes } from "../../presaleContractFunctions";
import { copyToClipboard } from "../../../../Helper/Helper";
import { convertToCSV, downloadCSV } from "./csvUtils";
import toast from "react-hot-toast";
import FileDownloadIcon from "@mui/icons-material/FileDownload";

const { abi: trinityManagerAbi } = trinityManagerArtifact;

const shorten = (value) =>
  value ? `${value.slice(0, 6)}...${value.slice(-4)}` : "--";

const formatDateTime = (timestamp) => {
  if (!timestamp) return "--";
  try {
    const date = new Date(Number(timestamp) * 1000);
    return date.toLocaleString();
  } catch {
    return "--";
  }
};

const formatBnb = (value) => {
  if (!value) return "0";
  try {
    return Number(ethers.utils.formatEther(value)).toFixed(4);
  } catch {
    return "0";
  }
};

const BSC_TX_BASE_URL = "https://bscscan.com/tx/";
const ITEMS_PER_PAGE = 10;

function PurchasesTable({ provider, signer, refreshKey, isOwner }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [hashMap, setHashMap] = useState({});
  const hashRef = useRef({});
  const [addressNames, setAddressNames] = useState({});
  const addressNamesRef = useRef({});
  const [downloadingCSV, setDownloadingCSV] = useState(false);
  const managerAddress = config.get("smartContract.TRINITY_MANAGER_ADDRESS");

  const fetchAddressNames = useCallback(async (addresses = []) => {
    if (!addresses.length) return;
    const normalized = Array.from(
      new Set(
        addresses
          .map((addr) => (typeof addr === "string" ? addr.toLowerCase() : null))
          .filter((addr) => addr && !addressNamesRef.current?.[addr])
      )
    );
    if (!normalized.length) return;
    try {
      const response = await request(
        endpoints.Users.GetNames.method,
        endpoints.Users.GetNames.url,
        { addresses: normalized }
      );
      const users = response?.data?.data?.users || [];
      const mapping = {};
      users.forEach((user) => {
        const lower = user?.wallet_address?.toLowerCase?.();
        if (lower) {
          mapping[lower] = user?.name || user?.username || user?.wallet_address;
        }
      });
      if (Object.keys(mapping).length) {
        addressNamesRef.current = {
          ...addressNamesRef.current,
          ...mapping,
        };
        setAddressNames((prev) => ({ ...prev, ...mapping }));
      }
    } catch (err) {
      console.error("Failed to fetch user names", err);
    }
  }, []);

  useEffect(() => {
    if (!provider || !managerAddress) return;
    if (!signer || !isOwner) {
      setRows([]);
      setTotal(0);
      setError("Connect with an authorized owner wallet to view purchases.");
      return;
    }

    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const contract = new ethers.Contract(
          managerAddress,
          trinityManagerAbi,
          signer
        );
        const pageNo = page - 1; // Convert to 0-based page number
        const [purchases, totalCount] = await contract.getAllPurchasesRange(
          ethers.BigNumber.from(String(pageNo)),
          ethers.BigNumber.from(String(ITEMS_PER_PAGE))
        );
        const parsed = purchases.map((purchase, index) => ({
          id: purchase.id?.toString() || `${pageNo * ITEMS_PER_PAGE + index}`,
          purchaseId: purchase.id?.toString() || "0",
          buyer: purchase.buyer,
          shares: purchase.shares?.toString() || "0",
          amount: purchase.amount,
          timestamp: purchase.timestamp?.toString() || "0",
        }));
        fetchAddressNames(parsed.map((p) => p.buyer));
        if (!cancelled) {
          setRows(parsed);
          setTotal(Number(totalCount) || 0);
        }
      } catch (err) {
        console.error("Failed to fetch purchases", err);
        if (!cancelled) {
          setError("Unable to fetch purchase transactions.");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [
    provider,
    signer,
    refreshKey,
    page,
    managerAddress,
    isOwner,
    fetchAddressNames,
  ]);

  useEffect(() => {
    const missing = rows
      .map((row) => Number(row.purchaseId))
      .filter((id) => id && !hashRef.current[id]);
    if (!missing.length) return;
    let cancelled = false;
    const loadHashes = async () => {
      try {
        const result = await getPurchaseHashes(missing);
        if (cancelled) return;
        const updated = { ...hashRef.current };
        if (Array.isArray(result)) {
          result.forEach((item) => {
            const transactionId = Number(item?.transaction_id);
            const hash = item?.transaction_hash;
            if (transactionId && hash) {
              updated[transactionId] = hash;
            }
          });
        } else if (result && typeof result === "object") {
          Object.entries(result).forEach(([key, value]) => {
            const transactionId = Number(key);
            if (transactionId && value) {
              updated[transactionId] = value;
            }
          });
        }
        hashRef.current = updated;
        setHashMap({ ...updated });
      } catch (err) {
        console.error("Failed to load purchase hashes", err);
      }
    };
    loadHashes();
    return () => {
      cancelled = true;
    };
  }, [rows]);

  const totalPages = Math.ceil(total / ITEMS_PER_PAGE);

  const handleDownloadCSV = useCallback(async () => {
    if (!provider || !managerAddress || !signer || !isOwner) {
      toast.error("Please connect with an authorized owner wallet.");
      return;
    }

    setDownloadingCSV(true);
    let loadingToast = null;
    try {
      const contract = new ethers.Contract(
        managerAddress,
        trinityManagerAbi,
        signer
      );

      // Get total count
      const [, totalCount] = await contract.getAllPurchasesRange(
        ethers.BigNumber.from("0"),
        ethers.BigNumber.from("1")
      );
      const total = Number(totalCount) || 0;

      if (total === 0) {
        toast("No purchases to export.");
        setDownloadingCSV(false);
        return;
      }

      loadingToast = toast.loading(`Fetching ${total} purchases...`);

      // Fetch all purchases in batches
      const allPurchases = [];
      const batchSize = 100;
      let currentPage = 0;

      while (allPurchases.length < total) {
        const [purchases] = await contract.getAllPurchasesRange(
          ethers.BigNumber.from(String(currentPage)),
          ethers.BigNumber.from(String(batchSize))
        );

        if (!purchases || purchases.length === 0) break;

        const parsed = purchases.map((purchase) => ({
          purchaseId: purchase.id?.toString() || "0",
          buyer: purchase.buyer,
          shares: purchase.shares?.toString() || "0",
          amount: purchase.amount,
          timestamp: purchase.timestamp?.toString() || "0",
        }));

        allPurchases.push(...parsed);
        currentPage += batchSize;

        // Fetch names for this batch
        await fetchAddressNames(parsed.map((p) => p.buyer));
      }

      // Fetch all transaction hashes
      const purchaseIds = allPurchases.map((p) => Number(p.purchaseId));
      const hashes = await getPurchaseHashes(purchaseIds);
      const hashMapForCSV = {};
      if (Array.isArray(hashes)) {
        hashes.forEach((item) => {
          const transactionId = Number(item?.transaction_id);
          const hash = item?.transaction_hash;
          if (transactionId && hash) {
            hashMapForCSV[transactionId] = hash;
          }
        });
      } else if (hashes && typeof hashes === "object") {
        Object.entries(hashes).forEach(([key, value]) => {
          const transactionId = Number(key);
          if (transactionId && value) {
            hashMapForCSV[transactionId] = value;
          }
        });
      }

      // Prepare CSV data
      const csvData = allPurchases.map((purchase) => {
        const lowerBuyer = purchase.buyer?.toLowerCase?.();
        const buyerName =
          lowerBuyer && addressNamesRef.current[lowerBuyer]
            ? addressNamesRef.current[lowerBuyer]
            : "";
        const txHash = hashMapForCSV[Number(purchase.purchaseId)] || "";
        return {
          txHash: txHash,
          date: formatDateTime(purchase.timestamp),
          buyerAddress: purchase.buyer || "",
          buyerName: buyerName,
          shares: purchase.shares || "0",
          amountBNB: formatBnb(purchase.amount),
        };
      });

      const headers = [
        { key: "txHash", label: "Tx Hash" },
        { key: "date", label: "Date" },
        { key: "buyerAddress", label: "Buyer Address" },
        { key: "buyerName", label: "Buyer Name" },
        { key: "shares", label: "Shares" },
        { key: "amountBNB", label: "Amount (BNB)" },
      ];

      const csvContent = convertToCSV(csvData, headers);
      const filename = `purchases_${
        new Date().toISOString().split("T")[0]
      }.csv`;
      downloadCSV(csvContent, filename);
      toast.dismiss(loadingToast);
      toast.success(`Successfully exported ${allPurchases.length} purchases.`);
    } catch (err) {
      console.error("Failed to export CSV", err);
      if (loadingToast) toast.dismiss(loadingToast);
      toast.error("Failed to export CSV. Check console for details.");
    } finally {
      setDownloadingCSV(false);
    }
  }, [provider, signer, managerAddress, isOwner, fetchAddressNames]);

  const body = useMemo(() => {
    const colSpan = 5;
    if (!provider || !managerAddress) {
      return (
        <tr>
          <td className="table-no-data" colSpan={colSpan}>
            Connect to the correct network to view purchases.
          </td>
        </tr>
      );
    }
    if (!signer || !isOwner) {
      return (
        <tr>
          <td className="table-no-data" colSpan={colSpan}>
            Connect with an authorized owner wallet to view purchases.
          </td>
        </tr>
      );
    }
    if (loading) {
      return (
        <tr>
          <td className="table-no-data" colSpan={colSpan}>
            Loading purchases...
          </td>
        </tr>
      );
    }
    if (error) {
      return (
        <tr>
          <td className="table-no-data" colSpan={colSpan}>
            {error}
          </td>
        </tr>
      );
    }
    if (!rows.length) {
      return (
        <tr>
          <td className="table-no-data" colSpan={colSpan}>
            No purchase transactions found.
          </td>
        </tr>
      );
    }

    return rows.map((row) => {
      const lowerBuyer = row.buyer?.toLowerCase?.();
      const buyerName = lowerBuyer ? addressNames[lowerBuyer] : null;
      const purchaseHash = hashMap[Number(row.purchaseId)];
      return (
        <tr key={row.id}>
          <td>
            {purchaseHash ? (
              <Tooltip title={purchaseHash} placement="top" arrow>
                <a
                  href={`${BSC_TX_BASE_URL}${purchaseHash}`}
                  target="_blank"
                  rel="noreferrer"
                  className="hash-link"
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    color: "#fff",
                    textDecoration: "none",
                  }}
                >
                  {shorten(purchaseHash)}
                  <LaunchIcon sx={{ fontSize: 16, color: "#fff" }} />
                </a>
              </Tooltip>
            ) : (
              <span>Loading...</span>
            )}
          </td>
          <td>{formatDateTime(row.timestamp)}</td>
          <td>
            <div style={{ display: "flex", alignItems: "flex-end", gap: 6 }}>
              <Tooltip title={row.buyer || "--"} placement="top" arrow>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    lineHeight: 1.1,
                  }}
                >
                  {buyerName && (
                    <span
                      style={{
                        fontSize: "1rem",
                        color: "#bfc3d9",
                        marginBottom: 4,
                      }}
                    >
                      {buyerName}
                    </span>
                  )}
                  <span style={{ fontSize: "0.75rem" }}>
                    {shorten(row.buyer)}
                  </span>
                </div>
              </Tooltip>
              {row.buyer && (
                <IconButton
                  className="totalPriceH2"
                  size="small"
                  onClick={() => copyToClipboard(row.buyer)}
                >
                  <ContentCopyIcon sx={{ fontSize: 16 }} />
                </IconButton>
              )}
            </div>
          </td>
          <td>{row.shares}</td>
          <td>{formatBnb(row.amount)} BNB</td>
        </tr>
      );
    });
  }, [
    rows,
    loading,
    error,
    provider,
    managerAddress,
    signer,
    isOwner,
    hashMap,
    addressNames,
  ]);

  return (
    <>
      <Card className="bg-live">
        <Card.Body>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "1rem",
            }}
          >
            <h5 className="mb-0">Purchases</h5>
            <button
              type="button"
              className="summary-copy-btn1"
              onClick={handleDownloadCSV}
              disabled={downloadingCSV || !isOwner || !signer || total === 0}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                opacity:
                  downloadingCSV || !isOwner || !signer || total === 0
                    ? 0.6
                    : 1,
                cursor:
                  downloadingCSV || !isOwner || !signer || total === 0
                    ? "not-allowed"
                    : "pointer",
              }}
            >
              <FileDownloadIcon sx={{ fontSize: 18 }} />
              {downloadingCSV ? "Exporting..." : "Download CSV"}
            </button>
          </div>
          <div className="table-responsive-wrapper">
            <table className="purchasing-table">
              <thead>
                <tr>
                  <th>Tx Hash</th>
                  <th>Date</th>
                  <th>Buyer</th>
                  <th>Shares</th>
                  <th>Amount (BNB)</th>
                </tr>
              </thead>
              <tbody>{body}</tbody>
            </table>
          </div>
        </Card.Body>
      </Card>
      {total > 0 && (
        <Pagination
          totalList={total}
          pages={totalPages}
          currentPage={page}
          setCurrentPage={setPage}
          itemsPerPage={ITEMS_PER_PAGE}
        />
      )}
    </>
  );
}

export default PurchasesTable;
